'use client';

import { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Check, Package } from 'lucide-react';
import { VatNotice } from '@/components/vat-notice';
import { PlaceholderMedia } from '@/components/ui/placeholder-media';
import { useTheme } from '@/context/theme-context';
import { useOwnedProducts } from '@/context/owned-products-context';
import { normalizeProductDoc, useProductsByIds, type Product } from '@/context/product-context';
import { skipImageOptimizer } from '@/lib/image-optimizer';
import {
  bundleItemLabel,
  bundleItemListPriceAud,
  bundleSaving,
  bundleSizeOf,
  isBundleProduct,
  resolveBundleItems,
  storedBundleItems,
  type BundleGrant,
  type BundleResolution,
} from '@/lib/product-bundles';

const NO_IDS: readonly string[] = [];

type SeedDoc = { id: string; data: Record<string, unknown> };

export type BundleView = {
  isBundle: boolean;
  resolution: BundleResolution;
  products: ReadonlyMap<string, Product>;
  isLoaded: boolean;
  /** What the products cost bought separately, and the saving, or null when the bundle saves nothing. */
  separateAud: number;
  saving: ReturnType<typeof bundleSaving>;
};

function imageSrcOf(product: Pick<Product, 'image'> | undefined): string | null {
  if (!product?.image) return null;
  return typeof product.image === 'string' ? product.image || null : product.image.src || null;
}

/**
 * A bundle's products as its page shows them (src/lib/product-bundles.ts): read
 * through useProductsByIds, starting from the server's copy when it handed one
 * over (page.tsx), so the first paint already lists them, then kept live. Only
 * the products that still count are listed: one archived or taken down drops
 * out until it is back, as it does from what the bundle sells.
 */
export function useBundleView(bundle: Product | null | undefined, seedItems?: readonly SeedDoc[]): BundleView {
  const isBundle = isBundleProduct(bundle);
  const refs = useMemo(() => storedBundleItems(bundle), [bundle]);
  const seed = useMemo(
    () => (seedItems && seedItems.length > 0
      ? new Map(seedItems.map(({ id, data }) => [id, normalizeProductDoc(id, data)]))
      : undefined),
    [seedItems],
  );
  const ids = useMemo(
    () => (!isBundle ? NO_IDS : seed ? Array.from(seed.keys()) : refs.map((ref) => ref.productId)),
    [isBundle, seed, refs],
  );
  const { products, isLoaded } = useProductsByIds(ids, { withDiscounts: true, seed });
  const resolution = useMemo(
    () => resolveBundleItems(typeof bundle?.sellerId === 'string' ? bundle.sellerId : '', refs, products as ReadonlyMap<string, unknown>),
    [bundle?.sellerId, refs, products],
  );
  const prices = resolution.items.map((item) => item.listPriceAud);
  const separateAud = Math.round(prices.reduce((sum, price) => sum + price, 0) * 100) / 100;
  const bundlePriceAud = bundle ? bundleItemListPriceAud(bundle, { productId: bundle.id }) : 0;
  return { isBundle, resolution, products, isLoaded, separateAud, saving: bundleSaving(bundlePriceAud, prices) };
}

/** "What's in this bundle": every product it holds, the tier in brackets, each linking to its own page. */
export function BundleContents({ view, bundleId }: { view: BundleView; bundleId: string }) {
  const { formatCurrency } = useTheme();
  const { isOwned } = useOwnedProducts();
  if (!view.isBundle) return null;
  const items = view.resolution.items;
  const ownsBundle = isOwned(bundleId);
  const ownedCount = ownsBundle ? 0 : items.filter((item) => isOwned(item.ref.productId)).length;
  return (
    <section className="not-prose mb-8 space-y-3" aria-labelledby="bundle-contents-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="bundle-contents-heading" className="font-seller-heading text-xl font-semibold">
          What&apos;s in this bundle{items.length > 0 ? ` (${items.length})` : ''}
        </h2>
        {view.saving && (
          <p className="text-sm text-muted-foreground">
            Save {view.saving.percent}% on buying them separately
          </p>
        )}
      </div>
      {ownedCount > 0 && (
        <p className="text-sm text-muted-foreground">
          You already own {ownedCount} of these {items.length}.
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{view.isLoaded ? 'This bundle has no products on sale right now.' : 'Loading the products...'}</p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {items.map((item) => {
            const product = view.products.get(item.ref.productId);
            const src = imageSrcOf(product);
            const label = bundleItemLabel(item.grant.productName || product?.name || '', item.grant.tierName);
            const owned = ownsBundle || isOwned(item.ref.productId);
            return (
              <li key={item.ref.productId} className="flex items-center gap-3 p-3">
                <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted">
                  {src ? (
                    <Image src={src} alt="" fill sizes="48px" className="object-cover" unoptimized={skipImageOptimizer(src)} />
                  ) : (
                    <PlaceholderMedia className="h-12 w-12" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <Link href={`/product/${item.ref.productId}`} className="block font-medium hover:underline [overflow-wrap:anywhere]">
                    {label}
                  </Link>
                  {owned && (
                    <span className="inline-flex items-center gap-1 text-xs text-success">
                      <Check className="h-3 w-3" aria-hidden /> In your library
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {item.listPriceAud > 0 ? (
                    <>
                      {formatCurrency(item.listPriceAud)} <VatNotice amount={item.listPriceAud} />
                    </>
                  ) : 'Free'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Buyers get every product here, and any the seller adds to the bundle later.
      </p>
    </section>
  );
}

/** Under the bundle's price: how many products, and what they cost bought separately. */
export function BundlePriceNote({ view }: { view: BundleView }) {
  const { formatCurrency } = useTheme();
  if (!view.isBundle || view.resolution.items.length === 0) return null;
  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground">
      <Package className="h-4 w-4" aria-hidden />
      <span>Bundle of {view.resolution.items.length} products.</span>
      {view.saving && (
        <span>
          Bought separately <span className="tabular-nums line-through">{formatCurrency(view.separateAud)}</span>{' '}
          <VatNotice amount={view.separateAud} />, you save {view.saving.percent}%.
        </span>
      )}
    </p>
  );
}

/**
 * On a product's own page: the seller's live bundles that hold it, as links,
 * from the server's read (page.tsx). The cross-sell that makes a bundle sell.
 */
export function AlsoInBundles({ bundles }: { bundles?: readonly SeedDoc[] | null }) {
  const { formatCurrency } = useTheme();
  const list = useMemo(
    () => (bundles ?? []).map(({ id, data }) => normalizeProductDoc(id, data)).filter((bundle) => isBundleProduct(bundle) && bundle.status === 'live'),
    [bundles],
  );
  if (list.length === 0) return null;
  return (
    <div className="mt-4 space-y-2">
      {list.slice(0, 3).map((bundle) => {
        const count = bundleSizeOf(bundle);
        const priceAud = bundleItemListPriceAud(bundle, { productId: bundle.id });
        return (
          <Link
            key={bundle.id}
            href={`/product/${bundle.id}`}
            className="flex items-start gap-2 rounded-lg border p-3 text-sm transition-colors hover:bg-accent"
          >
            <Package className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              Also in <span className="font-medium">{bundle.name || 'a bundle'}</span>
              {count > 0 ? `, ${count} products` : ''}
              {priceAud > 0 && (
                <> for <span className="tabular-nums">{formatCurrency(priceAud)}</span> <VatNotice amount={priceAud} /></>
              )}
            </span>
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Under a bundle's line at checkout and on the receipt: the products it
 * grants, each with its tier in brackets, as the server quoted (or fulfilled)
 * them. At checkout, `markOwned` says which the buyer already has, and when they
 * hold another tier, that the bundle adds its own.
 */
export function BundleLineContents({
  grants,
  markOwned = false,
}: {
  grants: readonly BundleGrant[];
  markOwned?: boolean;
}) {
  const { isOwned, ownedTierId } = useOwnedProducts();
  if (grants.length === 0) return null;
  return (
    <details className="text-[11px] text-muted-foreground">
      <summary className="cursor-pointer select-none">Includes {grants.length} product{grants.length === 1 ? '' : 's'}</summary>
      <ul className="mt-1 space-y-0.5 pl-3">
        {grants.map((grant) => {
          const label = bundleItemLabel(grant.productName, grant.tierName);
          let note = '';
          if (markOwned && isOwned(grant.productId)) {
            const held = ownedTierId.get(grant.productId);
            note = grant.tierId && held && held !== grant.tierId
              ? `you own another tier; this adds ${grant.tierName || 'its tier'}`
              : 'you own this';
          }
          return (
            <li key={grant.productId} className="min-w-0 [overflow-wrap:anywhere]">
              {label}{note ? <span className="text-muted-foreground"> ({note})</span> : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
